// src/features/hmis/components/HMIActions.tsx
import React, { useState } from 'react';
import {
  useTranslate,
  useRedirect,
  useDataProvider,
  useNotify,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VpnKey as ProvisionIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon, // ✅ CAMBIO: Icono añadido para el diálogo de confirmación
} from '@mui/icons-material';
import { useHMIPermissions } from '../hooks/useHMIPermissions';
import { HMIListItem } from '../types';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

interface HMIActionsProps {
  record: HMIListItem;
  small?: boolean;
}

const HMIActions: React.FC<HMIActionsProps> = ({ record, small }) => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { canEditHMIs, canDeleteHMIs, canGenerateProvisioningCode } = useHMIPermissions();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showProvisioningDialog, setShowProvisioningDialog] = useState(false);
  
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const [provisioningCode, setProvisioningCode] = useState<string>('');
  const [codeExpiresAt, setCodeExpiresAt] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  const open = Boolean(anchorEl);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleEdit = () => {
    handleClose();
    redirect('edit', 'hmis', record.id);
  };
  
  const handleDelete = () => {
    handleClose();
    setShowDeleteDialog(true);
  };
  
  const handleOpenProvisioningConfirmation = () => {
    handleClose();
    setShowConfirmationDialog(true);
  };

  const handleConfirmAndGenerateCode = async () => {
    setShowConfirmationDialog(false);
    setShowProvisioningDialog(true); 
    setLoading(true);
    setProvisioningCode('');
    setCodeExpiresAt('');
    
    try {
      const response = await dataProvider.update('hmis', {
        id: record.id,
        data: {},
        previousData: record,
        meta: { action: 'generate-provisioning-code' }
      });
      
      if (response.data) {
        setProvisioningCode(response.data.provisioningCode || response.data.code || '');
        setCodeExpiresAt(response.data.expiresAt || '');
        notify(translate('resources.hmis.messages.code_generated'), { type: 'success' });
      }
    } catch (error) {
      console.error('Error generating provisioning code:', error);
      notify(translate('resources.hmis.errors.code_generation_failed'), { type: 'error' });
      setShowProvisioningDialog(false); // Cerramos también el de mostrar código si hay error
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyCode = () => {
    if (provisioningCode) {
      navigator.clipboard.writeText(provisioningCode);
      notify(translate('resources.hmis.messages.code_copied'), { type: 'success' });
    }
  };
  
  const handleCloseProvisioningDialog = () => {
    setShowProvisioningDialog(false);
    setProvisioningCode('');
    setCodeExpiresAt('');
  };
  
  const formatExpirationDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', { /* ... */ });
  };
  
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu', 'Más opciones')}>
          <IconButton
            size={'small'}
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVertIcon fontSize={'small'} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        
        {canEditHMIs && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}
        
        {canGenerateProvisioningCode && (
          // ✅ CAMBIO: El onClick ahora llama a la función que abre la confirmación.
          <MenuItem onClick={handleOpenProvisioningConfirmation}>
            <ListItemIcon><ProvisionIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{translate('resources.hmis.actions.generate_provisioning_code')}</ListItemText>
          </MenuItem>
        )}
        
        {canDeleteHMIs && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{translate('ra.action.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">{translate('resources.hmis.dialogs.provisioning_confirmation.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('resources.hmis.dialogs.provisioning_confirmation.warning')}
          </Alert>
          <Typography>
            {translate('resources.hmis.dialogs.provisioning_confirmation.question')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmationDialog(false)}>
            {translate('resources.hmis.dialogs.provisioning_confirmation.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmAndGenerateCode} 
            variant="contained" 
            color="primary"
            autoFocus
          >
            {translate('resources.hmis.dialogs.provisioning_confirmation.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para el código de provisioning */}
      <Dialog
        open={showProvisioningDialog}
        onClose={handleCloseProvisioningDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ProvisionIcon />
            <Typography variant="h6">{translate('resources.hmis.dialogs.provisioning_code.title')}</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : provisioningCode ? (
            <>
             
              
              <TextField
                fullWidth
                label={translate('resources.hmis.dialogs.provisioning_code.label')}
                value={provisioningCode}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyCode}>
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: { fontFamily: 'monospace', fontSize: '1.2em' }
                }}
                sx={{ mt: 3, mb: 2 }}
              />
              
              {codeExpiresAt && (
                <Alert severity="warning">
                  {translate('resources.hmis.dialogs.provisioning_code.expires', {
                    date: formatExpirationDate(codeExpiresAt)
                  })}
                </Alert>
              )}
            </>
          ) : (
            <Alert severity="error">
              {translate('resources.hmis.dialogs.provisioning_code.error')}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          {provisioningCode && (
            <Button onClick={handleConfirmAndGenerateCode} startIcon={<RefreshIcon />}>
              {translate('resources.hmis.dialogs.provisioning_code.regenerate')}
            </Button>
          )}
          <Button onClick={handleCloseProvisioningDialog}>
            {translate('resources.hmis.dialogs.provisioning_code.close')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog de confirmación de eliminación */}
      {showDeleteDialog && (
        <BaseDeleteDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={() => setShowDeleteDialog(false)}
          resource="hmis"
          recordId={record.id}
          record={record}
          title={`Eliminar HMI ${record.name}`}
          message={`¿Estás seguro de que quieres eliminar el HMI "${record.name}"?`}
          warningMessage="Esta acción no se puede deshacer."
        />
      )}
    </>
  );
};

export default HMIActions;